import React from 'react';
import { client } from '~/api/client';
import { idempotencyKeys } from '~/lib/idempotency';
import {
  SubscriptionEntitlement,
  useGetAgencyProfileAssignmentsQuery,
  useGetAgencyUserImportSchemaQuery,
  useGetTeamsQuery,
  type AgencyUserFieldsFragment,
  type LoggedInAgencyUserFieldsFragment
} from '~/api/graphql';
import { CsvImport } from '~/components/csv-import';
import {
  commitUserImport,
  userImportSchema,
  userToRecord,
  type UserImportContext
} from './user-import-csv';

/**
 * CSV import into the agency's users. Adds and updates only: a user missing from the file
 * is never disabled.
 */
export const UserImport = ({
  users,
  agencyUser,
  onImported,
  onImportingChange
}: {
  users: AgencyUserFieldsFragment[];
  agencyUser: LoggedInAgencyUserFieldsFragment;
  onImported: () => void;
  onImportingChange?: (importing: boolean) => void;
}) => {
  const { entitlements, subscription } = agencyUser.agency;
  const hasProfiles = entitlements.includes(SubscriptionEntitlement.AgencyProfileManagement);
  const hasTeams = entitlements.includes(SubscriptionEntitlement.TeamManagement);

  const profilesQuery = useGetAgencyProfileAssignmentsQuery({
    skip: !hasProfiles,
    fetchPolicy: 'network-only'
  });
  const teamsQuery = useGetTeamsQuery({ skip: !hasTeams, fetchPolicy: 'network-only' });
  const schemaQuery = useGetAgencyUserImportSchemaQuery({ fetchPolicy: 'network-only' });
  // A batch sent again unchanged, as after a lost response, reuses its key.
  const batchKeys = React.useRef(idempotencyKeys());

  const context = React.useMemo<UserImportContext>(() => {
    const profiles = profilesQuery.data?.agencyProfiles ?? [];
    const teams = teamsQuery.data?.teams.filter((team) => !team.archivedAt);
    const profileOfUser = new Map<string, string>();
    for (const profile of profiles) {
      for (const user of profile.users) {
        profileOfUser.set(user.id, profile.id);
      }
    }
    const teamsOfUser = new Map<string, string[]>();
    for (const team of teams ?? []) {
      for (const member of team.members) {
        teamsOfUser.set(member.user.id, [...(teamsOfUser.get(member.user.id) ?? []), team.id]);
      }
    }
    return {
      profiles,
      teams: hasTeams ? (teams ?? []) : undefined,
      profileOfUser,
      teamsOfUser,
      selfEmail: agencyUser.email
    };
  }, [profilesQuery.data, teamsQuery.data, hasTeams, agencyUser.email]);

  const serverSchema = schemaQuery.data?.agencyUserImportSchema;
  const schema = React.useMemo(
    () => serverSchema && userImportSchema(serverSchema, context),
    [serverSchema, context]
  );

  if (profilesQuery.loading || teamsQuery.loading || !schema) {
    return null;
  }

  const seatsLeft = subscription
    ? Math.max(0, subscription.numSeats - subscription.numSeatsActive)
    : undefined;
  const userIdByEmail = new Map(users.map((user) => [user.email.trim().toLowerCase(), user.id]));

  return (
    <CsvImport
      listName="Users"
      schema={schema}
      currentData={users.map((user) => userToRecord(user, context))}
      maxAdded={seatsLeft}
      limitNote="add seats with Change Plan above"
      upToDateMessage="All users in this CSV have already been added."
      describeCommit={({ added }) =>
        added.length > 0
          ? `${added.length} ${added.length === 1 ? 'person' : 'people'} will be able to sign in with their email${
              seatsLeft === undefined
                ? ''
                : `, using ${added.length} of ${seatsLeft} remaining seats`
            }.`
          : undefined
      }
      onCommit={(changes, reportProgress) =>
        commitUserImport(changes, {
          client,
          context,
          userIdByEmail,
          reportProgress,
          idempotencyKeyFor: batchKeys.current.keyFor
        })
      }
      onCommittingChange={onImportingChange}
      onCommitted={() => {
        void profilesQuery.refetch();
        void teamsQuery.refetch();
        onImported();
      }}
    />
  );
};
